<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import { url } from '@/config/urls';
import Goods from '@parts/Goods.vue';

const screenId = 'IdenticalFormulationItemAppModal';

const props = defineProps({
    janCode: {
        type: String,
        default: null,
    },
    goodsTypes: {
        type: Array,
        default: () => [],
    },
    identicalFormulationListApiUrl: {
        type: String,
        default: url.api.identicalFormulationList,
    },
    includeSelf: {
        type: Boolean,
        default: false
    },
});

const emit = defineEmits(['select-drug', 'apiError', 'modalClosed']);

const selectedSearchCodeType = ref('9');
const drugs = ref([]);
const isFetching = ref(false);
const goodsScrollRef = ref(null);

let modalEl = null;
let hiddenHandler = null;

const fetchIdenticalList = async() => {
    if (!props.janCode) {
        drugs.value = [];
        return;
    }
    isFetching.value = true;
    try {
        const res = await axios.get(props.identicalFormulationListApiUrl, {
            params: {
                searchCodeType: Number(selectedSearchCodeType.value),
                janCode: props.janCode,
                includeSelf: props.includeSelf,
            },
        });
        drugs.value = res.data?.response?.identicalFormulationList ?? [];
    } catch (error) {
        // 同一成分検索はデータがない場合404を返すため、空で返すように調整する
        if (error?.response?.status === 404) {
            drugs.value = [];
        } else {
            emit('apiError', error);
            drugs.value = [];
        }
    } finally {
        isFetching.value = false;
    }
};

watch(() => props.janCode, (newJan, oldJan) => {
    if (newJan && newJan !== oldJan) {
        selectedSearchCodeType.value = '9';
    }
});

watch(selectedSearchCodeType, () => {
    nextTick(() => {
        if (goodsScrollRef.value) goodsScrollRef.value.scrollTop = 0;
    });
});

watch([() => props.janCode, selectedSearchCodeType], () => {
    if (props.janCode) {
        fetchIdenticalList();
    } else {
        drugs.value = [];
    }
}, { immediate: true });

const normalizedDrugs = computed(() => {
    return (drugs.value || []).map((drug) => ({
        ...drug,
        formalGoodsName: `${drug.formalGoodsName1 || ''} ${drug.formalGoodsName2 || ''}`.trim(),
        canOrder: drug.canOrder ?? true,
    }));
});

onMounted(() => {
    modalEl = document.getElementById('identicalFormulationItemAppModal');
    hiddenHandler = () => emit('modalClosed');
    modalEl?.addEventListener('hidden.bs.modal', hiddenHandler);
});

onBeforeUnmount(() => {
    if (modalEl && hiddenHandler) {
        modalEl.removeEventListener('hidden.bs.modal', hiddenHandler);
    }
});
</script>
<template>
    <div class="modal com_modal2 sp1" id="identicalFormulationItemAppModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    <h3 class="com_fs18 com_strong1 com_center1 com_mgb15 com_lh1">
                        商品を選択してください
                    </h3>
                </div>
                <div class="modal-body">
                    <select
                        id="identicalFormulationItemAppModalSearchCodeType"
                        v-model="selectedSearchCodeType"
                        class="form-select com_form1 wd1 com_mg_a com_mgb10"
                        :disabled="!janCode || isFetching"
                    >
                        <option value="7">【7桁】一般名</option>
                        <option value="8">【8桁】一般名＋剤型</option>
                        <option value="9">【9桁】一般名＋剤型+規格</option>
                    </select>
                    <div class="modal-goods-scroll" ref="goodsScrollRef">
                        <p v-if="isFetching" class="com_fs14 text-center com_mgb10">読み込み中です…</p>
                        <p v-else-if="janCode && normalizedDrugs.length === 0" class="com_fs14 text-center com_mgb10">
                            検索結果は0件です
                        </p>
                        <div v-for="(drug, index) in normalizedDrugs" :key="drug.janCode" class="bd1">
                            <Goods
                                :screenId="screenId"
                                :isPermittedOrder="true"
                                :goods="drug"
                                :index="index"
                                :goodsTypes="goodsTypes"
                                @select-drug="emit('select-drug', $event)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
